import React, { useEffect, useState } from "react";
import { X, User, Mail, Phone, MapPin, Building2, FileText, ArrowUpRight, Loader2, ShieldCheck, Home, Tag } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { trackBeginCheckout } from "../lib/analytics";

const emptyForm = {
  name: "",
  email: "",
  phone: "",
  discountCode: "",
  // Billing address (always required)
  billingAddress: "",
  billingCity: "",
  billingZip: "",
  billingProvince: "",
  billingCountry: "IT",
  // Fiscal
  wantsInvoice: false,
  codiceFiscale: "",
  ragioneSociale: "",
  partitaIva: "",
  codiceSdi: "",
  pecEmail: "",
};

export default function CheckoutModal({ open, onClose, productId, productName, productPrice }) {
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fire GA4 begin_checkout when modal opens
  useEffect(() => {
    if (open && productId) {
      trackBeginCheckout({ productId, productName, price: productPrice });
    }
  }, [open, productId, productName, productPrice]);

  if (!open) return null;

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));
  // Validation
  const missing = [];
  if (!form.name.trim()) missing.push("nome");
  if (!form.email.trim()) missing.push("email");
  if (!form.billingAddress.trim()) missing.push("indirizzo");
  if (!form.billingCity.trim()) missing.push("città");
  if (!form.billingZip.trim() || !/^\d{5}$/.test(form.billingZip.trim())) missing.push("CAP (5 cifre)");
  if (form.billingProvince.trim().length !== 2) missing.push("provincia (2 lettere)");
  if (form.wantsInvoice) {
    if (!form.ragioneSociale.trim()) missing.push("ragione sociale");
    const pivaClean = form.partitaIva.replace(/^IT/i, "").trim();
    if (pivaClean.length !== 11 || !/^\d{11}$/.test(pivaClean)) missing.push("P.IVA (11 cifre)");
    const sdi = form.codiceSdi.trim();
    const pec = form.pecEmail.trim();
    const sdiOk = sdi.length === 7 && /^[A-Z0-9]{7}$/i.test(sdi);
    const pecOk = pec.length > 0 && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(pec);
    if (!sdiOk && !pecOk) missing.push("SDI (7 caratteri) o PEC valida");
  } else if (form.codiceFiscale.trim().length !== 16) {
    missing.push("codice fiscale (16 caratteri)");
  }
  const canSubmit = missing.length === 0;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setLoading(true);
    setError(null);
    const orderData = {
      productId,
      productName,
      productPrice,
      customerEmail: form.email.trim(),
      customerName: form.name.trim(),
      customerPhone: form.phone.trim() || null,
      billingAddress: form.billingAddress.trim(),
      billingCity: form.billingCity.trim(),
      billingZip: form.billingZip.trim(),
      billingProvince: form.billingProvince.trim().toUpperCase(),
      billingCountry: form.billingCountry.trim().toUpperCase() || "IT",
      wantsInvoice: form.wantsInvoice,
      codiceFiscale: form.wantsInvoice ? null : form.codiceFiscale.trim().toUpperCase(),
      ragioneSociale: form.wantsInvoice ? form.ragioneSociale.trim() : null,
      partitaIva: form.wantsInvoice ? form.partitaIva.trim() : null,
      codiceSdi: form.wantsInvoice ? form.codiceSdi.trim().toUpperCase() || null : null,
      pecEmail: form.wantsInvoice ? form.pecEmail.trim() || null : null,
      discountCode: form.discountCode.trim().toUpperCase() || null,
    };
    onClose();
    setLoading(false);
    navigate("/checkout/pagamento", { state: { order: orderData } });
  };

  return (
    <div
      className="fixed inset-0 z-[110] flex items-start md:items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto py-8"
      onClick={() => !loading && onClose()}
    >
      <div
        className="relative w-full max-w-xl bg-[#0f0f0f] border border-white/10 rounded-2xl shadow-2xl my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/10 sticky top-0 bg-[#0f0f0f] rounded-t-2xl z-10">
          <div>
            <div className="font-mono-label text-white/40 text-[10px]">Iscrizione</div>
            <div className="font-display text-white text-xl mt-0.5">I tuoi dati</div>
          </div>
          {!loading && (
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/70 hover:text-white transition-colors"
              aria-label="Chiudi"
              data-testid="checkout-modal-close-btn"
            >
              <X size={16} />
            </button>
          )}
        </div>

        <div className="px-6 py-6">
          <div className="bg-[#141414] border border-white/10 rounded-xl p-4 mb-6 flex items-center justify-between">
            <div>
              <div className="font-mono-label text-white/40 text-[10px]">Riepilogo</div>
              <div className="font-display text-white text-base mt-1">{productName}</div>
            </div>
            <div className="font-display text-white text-2xl">&euro;{Number(productPrice).toFixed(0)}</div>
          </div>

          {/* Contact */}
          <SectionLabel>Contatti</SectionLabel>
          <div className="space-y-3">
            <Field name="name" label="Nome e cognome" icon={<User size={14} />} value={form.name} onChange={(v) => set("name", v)} placeholder="Mario Rossi" disabled={loading} testId="checkout-name-input" />
            <Field name="email" label="Email" type="email" icon={<Mail size={14} />} value={form.email} onChange={(v) => set("email", v)} placeholder="mario@studio.it" disabled={loading} testId="checkout-email-input" />
            <Field name="phone" label="Telefono (opzionale)" icon={<Phone size={14} />} value={form.phone} onChange={(v) => set("phone", v)} placeholder="+39 320 123 4567" disabled={loading} testId="checkout-phone-input" />
            <Field name="discountCode" label="Codice sconto (se ne hai uno)" icon={<Tag size={14} />} value={form.discountCode} onChange={(v) => set("discountCode", v.toUpperCase())} placeholder="ES. TORNA10-XXXX" disabled={loading} testId="checkout-discount-input" />
          </div>

          {/* Billing address */}
          <SectionLabel className="mt-6">Indirizzo di fatturazione</SectionLabel>
          <div className="space-y-3">
            <Field name="billingAddress" label="Via / Piazza" icon={<Home size={14} />} value={form.billingAddress} onChange={(v) => set("billingAddress", v)} placeholder="Via Roma 123" disabled={loading} testId="checkout-address-input" />
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px] gap-3">
              <Field name="billingCity" label="Città" icon={<MapPin size={14} />} value={form.billingCity} onChange={(v) => set("billingCity", v)} placeholder="Pisa" disabled={loading} testId="checkout-city-input" />
              <Field name="billingZip" label="CAP" value={form.billingZip} onChange={(v) => set("billingZip", v)} placeholder="56100" disabled={loading} testId="checkout-zip-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field name="billingProvince" label="Provincia (sigla)" value={form.billingProvince} onChange={(v) => set("billingProvince", v.toUpperCase().slice(0, 2))} placeholder="PI" disabled={loading} testId="checkout-province-input" />
              <Field name="billingCountry" label="Nazione" value={form.billingCountry} onChange={(v) => set("billingCountry", v.toUpperCase().slice(0, 2))} placeholder="IT" disabled={loading} testId="checkout-country-input" />
            </div>
          </div>

          {/* Fattura toggle */}
          <SectionLabel className="mt-6">Fatturazione</SectionLabel>
          <div className="grid grid-cols-2 gap-2 mb-4" data-testid="checkout-invoice-toggle">
            <button type="button" onClick={() => set("wantsInvoice", false)} className={`text-sm rounded-xl border px-4 py-3 text-left transition-colors ${!form.wantsInvoice ? "border-lime bg-lime/10 text-white" : "border-white/10 text-white/60 hover:border-white/30"}`} style={!form.wantsInvoice ? { borderColor: "#d4ff3d", background: "rgba(212,255,61,0.08)" } : {}} data-testid="checkout-invoice-no">
              <div className="font-display text-[13px] leading-tight">Ricevuta personale</div>
              <div className="text-[11px] text-white/50 mt-1">Solo codice fiscale</div>
            </button>
            <button type="button" onClick={() => set("wantsInvoice", true)} className={`text-sm rounded-xl border px-4 py-3 text-left transition-colors ${form.wantsInvoice ? "text-white" : "border-white/10 text-white/60 hover:border-white/30"}`} style={form.wantsInvoice ? { borderColor: "#d4ff3d", background: "rgba(212,255,61,0.08)" } : {}} data-testid="checkout-invoice-yes">
              <div className="font-display text-[13px] leading-tight">Fattura elettronica</div>
              <div className="text-[11px] text-white/50 mt-1">P.IVA + SDI o PEC</div>
            </button>
          </div>

          {!form.wantsInvoice ? (
            <div className="space-y-3">
              <Field name="codiceFiscale" label="Codice fiscale (16 caratteri)" icon={<FileText size={14} />} value={form.codiceFiscale} onChange={(v) => set("codiceFiscale", v.toUpperCase())} placeholder="RSSMRA85M01H501U" disabled={loading} testId="checkout-cf-input" maxLength={16} />
            </div>
          ) : (
            <div className="space-y-3">
              <Field name="ragioneSociale" label="Ragione sociale / Denominazione" icon={<Building2 size={14} />} value={form.ragioneSociale} onChange={(v) => set("ragioneSociale", v)} placeholder="Rossi Srl" disabled={loading} testId="checkout-rs-input" />
              <Field name="partitaIva" label="Partita IVA (11 cifre)" icon={<FileText size={14} />} value={form.partitaIva} onChange={(v) => set("partitaIva", v.replace(/[^0-9IT]/gi, "").toUpperCase())} placeholder="02134640461" disabled={loading} testId="checkout-piva-input" maxLength={13} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field name="codiceSdi" label="Codice SDI (7 caratteri)" value={form.codiceSdi} onChange={(v) => set("codiceSdi", v.toUpperCase().slice(0, 7))} placeholder="0000000" disabled={loading} testId="checkout-sdi-input" maxLength={7} />
                <Field name="pecEmail" label="oppure PEC" type="email" value={form.pecEmail} onChange={(v) => set("pecEmail", v)} placeholder="azienda@pec.it" disabled={loading} testId="checkout-pec-input" />
              </div>
              <div className="text-white/40 text-[11px]">Compila almeno uno tra Codice SDI o PEC.</div>
            </div>
          )}

          {error && (
            <div className="mt-4 p-3 rounded-lg border border-red-400/30 bg-red-500/10 text-red-300 text-sm">{error}</div>
          )}

          {!canSubmit && missing.length > 0 && (
            <div className="mt-4 text-white/50 text-xs">
              Campi mancanti: <span className="text-white/80">{missing.join(", ")}</span>
            </div>
          )}

          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit || loading}
            data-testid="checkout-submit-btn"
            className={`btn-primary mt-6 w-full justify-center ${!canSubmit || loading ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            {loading ? (
              <><Loader2 size={16} className="animate-spin" /> Apro il checkout...</>
            ) : (
              <>Vai al pagamento sicuro <ArrowUpRight size={16} /></>
            )}
          </button>

          <div className="mt-4 flex items-center justify-center gap-2 text-white/50 text-xs">
            <ShieldCheck size={13} style={{ color: "#d4ff3d" }} />
            Pagamento sicuro on-site &mdash; carta, PayPal, Apple/Google Pay
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionLabel({ children, className = "" }) {
  return (
    <div className={`font-mono-label text-white/40 text-[10px] tracking-widest mb-3 ${className}`}>
      {children}
    </div>
  );
}

function Field({ label, icon, value, onChange, placeholder, type = "text", disabled, testId, maxLength }) {
  return (
    <div>
      <label className="block font-mono-label text-white/50 text-[11px] mb-2 flex items-center gap-2">
        {icon} {label}
      </label>
      <input
        type={type}
        value={value}
        disabled={disabled}
        maxLength={maxLength}
        data-testid={testId}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-[#141414] border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors disabled:opacity-50"
      />
    </div>
  );
}
